/* eslint-disable @typescript-eslint/no-use-before-define */
import { green } from '@mui/material/colors';
import { isEmpty, orderBy, toNumber } from 'lodash';
import { Connection, Edge, Node, Position, ReactFlowInstance } from 'reactflow';
import {
	BIOMETRICS_GROUP_INTERACTION_ID,
	END_GROUP_TASK_SUFFIX,
	END_TASK_ID,
	HiddenActionInputs,
	IEdgeData,
	IFlowAction,
	IFlowActions,
	IFlowDSL,
	IFlowInteractionGroup,
	IFlowScreens,
	IFlowsMetadata,
	INodeData,
	IScreenMetadata,
	ITaskInteraction,
	ITaskNodeData,
	NEW_SCREEN_PREFIX,
	NextActionTaskType,
	OAUTH_GROUP_INTERACTION_ID,
	POLLING_GROUP_INTERACTION_ID,
	Rule,
	START_TASK_ID,
	Task,
	Tasks,
	TaskType,
	VariableType
} from 'types/flows';
import { ITheme } from 'types/theme';

const sortInteractions = (interactions?: ITaskInteraction[]) =>
	orderBy(interactions, ['label', 'icon'], 'asc');

const extractScreenInteractions = (
	tasks: Tasks | undefined,
	task: Task,
	screen?: IScreenMetadata
) => {
	const interactions: ITaskInteraction[] = [];
	let oauthInteraction: IFlowInteractionGroup | undefined;
	let biometricsInteraction: IFlowInteractionGroup | undefined;
	let pollingInteraction: IFlowInteractionGroup | undefined;

	screen?.interactions?.forEach((interaction) => {
		if (interaction.subType === 'social') {
			if (!oauthInteraction) {
				oauthInteraction = {
					id: OAUTH_GROUP_INTERACTION_ID,
					interactions: [],
					label: 'Socials',
					isCollapsible: true
				};
			}
			if (!oauthInteraction.nextTask) {
				oauthInteraction.nextTask = getNextTask(tasks, task, interaction.id);
			}
			oauthInteraction.interactions.push(interaction);
		} else if (
			interaction.icon === 'fingerprint' ||
			interaction.icon === 'passkey'
		) {
			if (!biometricsInteraction) {
				biometricsInteraction = {
					id: BIOMETRICS_GROUP_INTERACTION_ID,
					interactions: [],
					label: 'Biometrics',
					icon: 'fingerprint'
				};
			}
			if (!biometricsInteraction.nextTask) {
				biometricsInteraction.nextTask = getNextTask(
					tasks,
					task,
					interaction.id
				);
			}
			biometricsInteraction.interactions.push(interaction);
		} else if (interaction.id === 'polling') {
			if (!pollingInteraction) {
				pollingInteraction = {
					id: POLLING_GROUP_INTERACTION_ID,
					interactions: [],
					label: 'Polling'
				};
			}
			if (!pollingInteraction.nextTask) {
				pollingInteraction.nextTask = getNextTask(tasks, task, interaction.id);
			}
			pollingInteraction.interactions.push(interaction);
		} else {
			interactions.push({
				...interaction,
				nextTask: getNextTask(tasks, task, interaction.id)
			});
		}
	});
	if (oauthInteraction) {
		if (!oauthInteraction.nextTask) {
			oauthInteraction.nextTask = getNextTask(tasks, task, oauthInteraction.id);
		}
		interactions.push(oauthInteraction);
	}

	if (biometricsInteraction) {
		if (!biometricsInteraction.nextTask) {
			biometricsInteraction.nextTask = getNextTask(
				tasks,
				task,
				biometricsInteraction.id
			);
		}
		interactions.push(biometricsInteraction);
	}

	if (pollingInteraction) {
		if (!pollingInteraction.nextTask) {
			pollingInteraction.nextTask = getNextTask(
				tasks,
				task,
				pollingInteraction.id
			);
		}
		interactions.push(pollingInteraction);
	}

	return interactions;
};

const getDefaultStartNode = (dsl: IFlowDSL) =>
	createNewNode(
		START_TASK_ID,
		{
			name: START_TASK_ID,
			next: { rules: [{ taskId: dsl.startTask }] },
			view: { x: 0, y: 0 }
		},
		dsl.tasks,
		{ interactions: [{ id: '', nextTask: dsl.tasks?.[dsl.startTask] }] }
	);

const getNextTask = (
	tasks: Tasks | undefined,
	currentTask: Task,
	nextInteractionId: string
) => {
	const nextTaskId = currentTask.next?.rules?.find(
		(next) => (next.interactionId || '') === nextInteractionId
	)?.taskId;

	return nextTaskId ? tasks![nextTaskId] : undefined;
};

const buildInteraction = (edge: Edge<IEdgeData>) => ({
	interactionId: edge.label as string,
	taskId: edge.target
});

const extractTask = (
	node: Node<ITaskNodeData>,
	edges: Edge<IEdgeData>[]
): Task => {
	const currentEdges = edges
		.filter((edge) => edge.source === node.id)
		.reduce<Record<string, Edge<IEdgeData>>>(
			(prev, curr) => ({ ...prev, [(curr.label as string) || '']: curr }),
			{}
		);
	const rules: Rule[] = [];
	if (node.type === NodeTypes.startNode) {
		rules.push(
			...Object.values(currentEdges).map((edge) => buildInteraction(edge))
		);
	} else {
		node.data.interactions?.forEach((interaction) => {
			if (!currentEdges[interaction.id]) return;
			if ((interaction as IFlowInteractionGroup).interactions?.length > 0) {
				rules.push(
					...(interaction as IFlowInteractionGroup).interactions.map((i) => ({
						...buildInteraction(currentEdges[interaction.id]),
						interactionId: i.id
					}))
				);
			} else {
				rules.push(buildInteraction(currentEdges[interaction.id]));
			}
		});
	}
	const ret = {
		...node.data.task,
		ID: undefined, // clean this key if it exists
		name: node.data.task?.name || node.data.label || '',
		id: node.id,
		view: {
			...node.position
		},
		next: {
			rules
		}
	} as Task;

	return ret;
};

const getMemberTask = (
	parent: Node<ITaskNodeData>,
	edges: Edge<IEdgeData>[],
	memberAction: IFlowAction,
	endStep: boolean
): Task =>
	({
		name: memberAction.displayName,
		...parent.data.task,
		ID: undefined, // clean this key if it exists
		id: endStep ? `${parent.id}${END_GROUP_TASK_SUFFIX}` : parent.id,
		action: memberAction.name,
		view: {
			...parent.position
		},
		screenId: undefined, // clean this key if it exists
		arguments: getTaskArgsFromAction(memberAction),
		next: {
			rules: edges
				.filter((edge) => edge.source === parent.id)
				.filter((edge) => {
					const interactionId = extractInteractionId(edge);
					return (
						(interactionId &&
							memberAction.errors?.some((err) => err.type === interactionId)) ||
						edge.label ===
							`${NextActionTaskType.success}.${endStep ? '1' : '0'}`
					);
				})
				.map((edge) => ({
					interactionId: extractInteractionId(edge)?.split('.')[0],
					taskId: edge.target
				}))
		}
	} as Task);

const extractMemberTasks = (
	parent: Node<ITaskNodeData>,
	edges: Edge<IEdgeData>[],
	members: string[],
	actions: IFlowActions
): Task[] => {
	const [startTaskName, endTaskName] = members;
	const startTaskAction = actions?.[startTaskName];
	const endTaskAction = actions?.[endTaskName];

	const startTask = getMemberTask(parent, edges, startTaskAction, false);
	const endTask = getMemberTask(parent, edges, endTaskAction, true);

	delete startTask.members;
	delete endTask.members;

	Object.keys(startTask.arguments || {}).forEach((key) => {
		const parentArg = parent.data.task?.arguments?.[key];
		if (parentArg?.value) {
			startTask.arguments![key].value = parentArg.value;
		}
		if (Object.keys(HiddenActionInputs).includes(key)) {
			startTask.arguments![key].value = endTask.id!;
		}
	});

	startTask.next!.rules.push({
		interactionId: NextActionTaskType.success,
		taskId: endTask.id!
	});

	return [startTask, endTask];
};

const composeGroupTask = (
	groupId: string,
	task: Task,
	actions: IFlowActions,
	tasks: Tasks
): Task => {
	const groupAction = actions[groupId];
	const firstAction = groupAction?.members?.[0]
		? actions[groupAction?.members?.[0]]
		: undefined;
	const newTask = { ...task };
	const endTask = tasks![`${task.id}${END_GROUP_TASK_SUFFIX}`];
	newTask.action = groupId;
	newTask.name =
		firstAction?.displayName === newTask.name
			? groupAction?.displayName
			: newTask.name;
	newTask.next = {
		rules: generateRules(newTask.next?.rules, 0).concat(
			generateRules(endTask?.next?.rules, 1)
		)
	};
	newTask.arguments = { ...newTask.arguments, ...endTask?.arguments };
	return newTask;
};

const generateRules = (rules?: Rule[], index?: number) =>
	(rules || []).map((rule) => ({
		...rule,
		interactionId:
			rule.interactionId === NextActionTaskType.success
				? `${rule.interactionId}${index !== undefined ? `.${index}` : ''}`
				: rule.interactionId
	}));

const reduceInteractions = (
	interactions?: ITaskInteraction[],
	parentId?: string
): Record<string, string> =>
	interactions?.reduce(
		(prev, curr) => ({
			...prev,
			...((curr as IFlowInteractionGroup).interactions?.length > 0
				? reduceInteractions(
						(curr as IFlowInteractionGroup).interactions,
						curr.id
				  )
				: { [curr.id]: parentId || curr.id })
		}),
		{}
	) || {};

export enum NodeTypes {
	actionNode = 'actionablenode',
	startNode = 'start',
	endNode = 'end'
}

export const extractFlowTasks = (
	nodes: Node<ITaskNodeData>[],
	edges: Edge<IEdgeData>[],
	actions?: IFlowActions
): Tasks =>
	nodes.reduce((prev, current) => {
		const members = current.data.task?.members;
		if (!members) {
			return { ...prev, [current.id]: extractTask(current, edges) };
		}
		const [startTask, endTask] = extractMemberTasks(
			current,
			edges,
			members,
			actions!
		);

		return {
			...prev,
			[startTask.id!]: startTask,
			[endTask.id!]: endTask
		};
	}, {});

export const extractFlowDSL = (
	rfInstance: ReactFlowInstance,
	actions?: IFlowActions
): IFlowDSL | null => {
	if (!rfInstance) return null;
	const { nodes, edges } = rfInstance.toObject();
	return {
		startTask:
			edges.find((edge) => edge.source === START_TASK_ID)?.target || '0',
		tasks: extractFlowTasks(nodes, edges, actions)
	};
};

export const getNextTaskId = (tasks: Tasks | undefined): string =>
	Object.keys(tasks || {}).reduce((prev, curr) => {
		const nextNumber = toNumber(curr) + 1;
		return toNumber(prev) < nextNumber ? `${nextNumber}` : prev;
	}, '0');

export const extractInteractionId = (edge: Edge | Connection) =>
	(edge.sourceHandle || '').split('label-').pop() || '';

export const generateEdgeId = (
	sourceId: string,
	targetId: string,
	interactionId?: string
) => `edge-${sourceId}-${targetId}-${interactionId || ''}`;

export const generateNodeId = (nodeId: string) => `node-${nodeId}`;

export const generateSourceHandleId = (
	nodeId: string,
	interactionId?: string
) => `handler-node-${nodeId}-label-${interactionId || ''}`;

export const getTaskColor = (theme: ITheme, taskType?: TaskType | string) => {
	switch (taskType) {
		case TaskType.screen:
			return theme.palette.primary.main;
		case TaskType.condition:
			return green[400];
		case TaskType.automated:
		default:
			return theme.palette.secondary.main;
	}
};

export const getTaskInteractions = (
	task: Task,
	tasks?: Tasks,
	metadata?: IFlowsMetadata,
	screens?: IFlowScreens
): ITaskInteraction[] => {
	const { actions } = metadata || {};
	if (task.id === START_TASK_ID) {
		return [{ id: '', nextTask: getNextTask(tasks, task, '') }];
	}

	switch (task.type) {
		case TaskType.condition: {
			return (
				task.conditions?.map((condition) => ({
					id: condition.interactionId,
					label: condition.name,
					nextTask: getNextTask(tasks, task, condition.interactionId)
				})) || []
			);
		}

		case TaskType.automated: {
			const action = actions?.[task.action!];
			if (!action) return [];

			const successes: ITaskInteraction[] = [];

			if (action.successes) {
				action.successes.forEach((success, i) => {
					let id = success.id || NextActionTaskType.success;
					if (!isEmpty(action.members)) {
						id +=
							action.members!.length === action.successes!.length
								? `.${i}`
								: `.${action.successes!.length - i}`;
					}
					successes.push({
						id,
						label: success.displayName,
						type: NextActionTaskType.success,
						nextTask: getNextTask(tasks, task, id)
					});
				});
			} else {
				successes.push({
					id: NextActionTaskType.success,
					type: NextActionTaskType.success,
					nextTask: getNextTask(tasks, task, NextActionTaskType.success)
				});
			}

			const errors = sortInteractions(
				action.errors?.map<ITaskInteraction>((err) => ({
					id: err.type,
					label: err.displayName,
					type: NextActionTaskType.error,
					nextTask: getNextTask(tasks, task, err.type)
				}))
			);

			return [...successes, ...errors];
		}

		case TaskType.screen:
		default: {
			const screen = screens?.[task.screenId!];
			return sortInteractions(extractScreenInteractions(tasks, task, screen));
		}
	}
};

export const attachNewEdgeInteractions = (
	node: Node<INodeData>,
	newEdge: Edge<IEdgeData>
) =>
	node.data.interactions?.map((interaction) => ({
		...interaction,
		nextTask:
			generateSourceHandleId(node.id, interaction.id) === newEdge.sourceHandle
				? newEdge.data?.nextTask
				: interaction.nextTask
	}));

export const detachEdgeInteractions = (
	node: Node<INodeData>,
	edge: Edge<any> | Connection
) =>
	node.data.interactions?.map((interaction) => ({
		...interaction,
		nextTask:
			generateSourceHandleId(node.id, interaction.id) === edge.sourceHandle
				? undefined
				: interaction.nextTask
	}));

export const getTaskArgsFromAction = (action?: IFlowAction) => {
	if (!action) {
		return {};
	}

	return action?.inputs
		?.filter((input) => input.visible)
		.reduce(
			(prev, curr) => ({
				...prev,
				[curr.name]: { value: '', type: VariableType.inline }
			}),
			{}
		);
};

export const createNewNode = (
	id: string,
	task: Task,
	tasks?: Tasks,
	options?: Partial<ITaskNodeData>,
	metadata?: IFlowsMetadata,
	screens?: IFlowScreens,
	nodeCount?: number
): Node<ITaskNodeData> => {
	const action = metadata?.actions[task.action || ''];
	if (action?.members) {
		task.members = action.members;
	}
	let type = NodeTypes.actionNode;
	if (id === START_TASK_ID) {
		type = NodeTypes.startNode;
	} else if (action?.name === END_TASK_ID) {
		type = NodeTypes.endNode;
	}
	return {
		id,
		position: {
			x:
				task.view?.x === undefined
					? 150 + (nodeCount || 0) * 400
					: task.view?.x,
			y: task.view?.y === undefined ? -50 : task.view?.y
		},
		type,
		targetPosition: Position.Right,
		deletable: type === NodeTypes.actionNode,
		data: {
			task: {
				...task,
				id
			},
			label: task.name,
			interactions: getTaskInteractions(task, tasks, metadata, screens),
			...options
		}
	};
};

export const generateEdge = (
	id: string,
	rule: Rule,
	tasks?: Tasks,
	options?: Partial<IEdgeData>
): Edge<IEdgeData> => ({
	id: generateEdgeId(id, rule.taskId, rule.interactionId),
	source: id,
	sourceHandle: generateSourceHandleId(id, rule.interactionId),
	target: rule.taskId,
	animated: false,
	label: rule.interactionId,
	type: 'buttonedge',
	data: {
		type: tasks![id]?.type,
		nextTask: tasks![rule.taskId],
		...options,
		onDelete: () => {}
	}
});

export const extractGraph = (
	dsl: IFlowDSL,
	options?: Partial<ITaskNodeData>,
	metadata?: IFlowsMetadata,
	screens?: IFlowScreens
): { initialNodes: Node<INodeData>[]; initialEdges: Edge<any>[] } => {
	if (isEmpty(dsl.tasks)) {
		return { initialNodes: [getDefaultStartNode(dsl)], initialEdges: [] };
	}

	const edges: Edge<IEdgeData>[] = !dsl.tasks[START_TASK_ID]
		? [generateEdge(START_TASK_ID, { taskId: dsl.startTask }, dsl.tasks)]
		: [];
	const nodes: Node<INodeData>[] = !dsl.tasks[START_TASK_ID]
		? [getDefaultStartNode(dsl)]
		: [];
	let nodeCount = 0;

	const actions = metadata?.actions || {};
	const groupTasks: Record<string, IFlowAction> = {};
	Object.entries(actions).forEach(([, action]) => {
		action.members?.forEach((member) => {
			groupTasks[member] = action;
		});
	});

	Object.entries(dsl.tasks).forEach(([id, task]) => {
		task.id = id;
		const groupingAction = groupTasks[task.action!];
		if (
			groupingAction?.members &&
			groupingAction.members[groupingAction.members.length - 1] === task.action
		)
			return;
		const readyTask = !groupingAction
			? task
			: composeGroupTask(groupingAction.name, task, actions, dsl.tasks!);
		const node: Node<ITaskNodeData> = createNewNode(
			id,
			readyTask,
			dsl.tasks,
			options,
			metadata,
			screens,
			nodeCount
		);

		const interactions = reduceInteractions(node.data.interactions);
		const edgesToAdd: Record<string, Edge<IEdgeData>> = {};
		readyTask.next?.rules?.forEach((rule) => {
			if (rule.taskId.endsWith(END_GROUP_TASK_SUFFIX)) return;
			const ruleToUse = { ...rule };
			if (rule.interactionId && interactions[rule.interactionId]) {
				ruleToUse.interactionId = interactions[rule.interactionId];
			}
			const edge = generateEdge(id, ruleToUse, dsl.tasks);
			edgesToAdd[edge.id] = edge;
		});
		nodes.push(node);
		edges.push(...Object.values(edgesToAdd));
		nodeCount += 1;
	});

	return { initialNodes: nodes, initialEdges: edges };
};

export const getAllTasks = (
	rfInstance: ReactFlowInstance<ITaskNodeData, IEdgeData> | null
) =>
	rfInstance
		?.getNodes()
		.reduce<Tasks>(
			(prev, curr) =>
				curr.data.task ? { ...prev, [curr.id]: curr.data.task } : prev,
			{}
		) || {};

export const getGraphView = (
	rfInstance: ReactFlowInstance<ITaskNodeData, IEdgeData> | null
) => {
	let maxX = -9999;
	let maxY = -9999;
	let minX = 9999;
	let minY = 9999;
	rfInstance?.getNodes().forEach((node) => {
		maxX = Math.max(node.position.x, maxX);
		maxY = Math.max(node.position.y, maxY);
		minX = Math.min(node.position.x, minX);
		minY = Math.min(node.position.y, minY);
	});
	return { maxX, maxY, minX, minY };
};

export const generateActionTask = (
	actionId: string,
	action?: IFlowAction
): Task => ({
	name: action?.displayName || action?.name || '',
	arguments: getTaskArgsFromAction(action),
	action: actionId,
	type: TaskType.automated
});

export const generateScreenTask = (name: string): Task => ({
	name,
	arguments: {},
	type: TaskType.screen,
	screenId: NEW_SCREEN_PREFIX
});

export const getErrorMessage = (err: any, defaultMsg: string): string =>
	err?.response?.data?.message || defaultMsg;
